import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, Polyline, TileLayer, useMap } from "react-leaflet";
import type { LatLngExpression } from "leaflet";
import L, { latLngBounds } from "leaflet";
import { Link } from "react-router";
import { ArrowLeft, List, LinkExternal01 } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { ProgressBar } from "@/components/base/progress-indicators/progress-indicators";
import { Button } from "@/components/base/buttons/button";
import { containerService } from "@/data/mock-containers";
import { documentService } from "@/data/mock-documents";
import { accountService } from "@/data/mock-accounts";
import type { Container, Document } from "@/types/container";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/hooks/use-i18n";
import { useResizeObserver } from "@/hooks/use-resize-observer";
import { Clock } from "@untitledui/icons";
import { generateRealisticRoute } from "@/utils/route-generator";
import { ContainerInfoPanel } from "@/components/application/map/container-info-panel";

const ROUTE_COLORS = [
    "#2563eb", // blue-600
    "#16a34a", // green-600
    "#db2777", // pink-600
    "#f59e0b", // amber-500
    "#7c3aed", // violet-600
    "#059669", // emerald-600
    "#dc2626", // red-600
    "#0ea5e9", // sky-500
];

function clamp(value: number, min: number, max: number) {
    return Math.max(min, Math.min(max, value));
}

function interpolate(a: [number, number], b: [number, number], t: number): [number, number] {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

function FitBounds({
    points,
    isPanelOpen,
    selectedContainerId,
    routes
}: {
    points: [number, number][];
    isPanelOpen: boolean;
    selectedContainerId?: string | null;
    routes?: Array<{
        c: Container;
        waypoints: any;
        coordinates: [number, number][];
        from: [number, number];
        to: [number, number];
        color: string;
    }>;
}) {
    const map = useMap();

    // Determina quali coordinate usare per il bounds
    const targetPoints = useMemo(() => {
        if (selectedContainerId && routes) {
            // Se c'è un container selezionato, usa solo le sue coordinate
            const selectedRoute = routes.find(route => route.c.id === selectedContainerId);
            if (selectedRoute) {
                return selectedRoute.coordinates;
            }
        }
        return points;
    }, [selectedContainerId, routes, points]);

    useEffect(() => {
        if (!targetPoints.length) return;
        const timer = setTimeout(() => {
            // Calcola bounds con padding asimmetrico quando il pannello è aperto
            const bounds = latLngBounds(targetPoints);

            if (isPanelOpen) {
                // Quando il pannello è aperto, aggiungi padding extra a sinistra
                // Usiamo un approccio manuale: espandiamo i bounds verso destra
                // per compensare il padding a sinistra
                const sw = bounds.getSouthWest();
                const ne = bounds.getNorthEast();
                const width = ne.lng - sw.lng;
                const height = ne.lat - sw.lat;

                // Espandiamo i bounds a destra per compensare il pannello a sinistra
                // Questo fa sì che la mappa mostri più area a destra, mantenendo i marker visibili
                const expandedBounds = latLngBounds(
                    [sw.lat - height * 0.1, sw.lng - width * 0.1], // Piccolo padding standard
                    [ne.lat + height * 0.1, ne.lng + width * 0.3]  // Espandi a destra
                );

                map.fitBounds(expandedBounds, {
                    padding: [40, 40],
                    maxZoom: 4 // Limita lo zoom out eccessivo
                });
            } else {
                map.fitBounds(bounds, {
                    padding: [40, 40],
                    maxZoom: 4
                });
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [map, JSON.stringify(targetPoints), isPanelOpen, selectedContainerId]);
    return null;
}

function MapResizeHandler({ wrapperRef, isPanelOpen }: { wrapperRef: React.RefObject<HTMLDivElement | null>; isPanelOpen: boolean }) {
    const map = useMap();

    // Invalida la size quando il container cambia dimensione
    useResizeObserver({
        ref: wrapperRef,
        onResize: () => {
            setTimeout(() => {
                map.invalidateSize();
            }, 0);
        },
    });

    // Invalida la size quando il pannello si apre/chiude
    useEffect(() => {
        const timer = setTimeout(() => {
            map.invalidateSize();
            // Riposiziona il centro dopo il ridimensionamento
            if (map.getCenter) {
                const currentCenter = map.getCenter();
                map.setView(currentCenter, map.getZoom());
            }
        }, 350); // Dopo la transizione (300ms)
        return () => clearTimeout(timer);
    }, [map, isPanelOpen]);

    // Forza invalidateSize al mount e dopo che il DOM è stabile
    useEffect(() => {
        const timers = [
            setTimeout(() => map.invalidateSize(), 0),
            setTimeout(() => map.invalidateSize(), 100),
            setTimeout(() => map.invalidateSize(), 300),
        ];
        return () => {
            timers.forEach(timer => clearTimeout(timer));
        };
    }, [map]);

    return null;
}


function getStatusColor(status: string) {
    switch (status) {
        case 'In Transit': return 'brand';
        case 'Delivered': return 'success';
        case 'Delayed': return 'error';
        case 'At Port': return 'warning';
        default: return 'gray';
    }
}

function getRiskLevelColor(riskLevel: string) {
    switch (riskLevel) {
        case 'Low': return 'success';
        case 'Medium': return 'warning';
        case 'High': return 'error';
        case 'Critical': return 'error';
        default: return 'gray';
    }
}

function getDocumentStatusColor(status: string) {
    switch (status) {
        case 'Completo': return 'success';
        case 'Pending': return 'warning';
        case 'Error': return 'error';
        default: return 'gray';
    }
}

export function ShipperContainersWorldMapPage() {
    const { t } = useTranslation(['pages:containers', 'common']);
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();
    const all: Container[] = containerService.getAll();
    const [progressById, setProgressById] = useState<Record<string, number>>(() => {
        const initial: Record<string, number> = {};
        for (const c of all) initial[c.id] = clamp((c.progress ?? 0) / 100, 0, 0.99);
        return initial;
    });
    const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
    const [isListPanelOpen, setIsListPanelOpen] = useState(false);
    const [hoveredContainerId, setHoveredContainerId] = useState<string | null>(null);
    const [highlightedRouteId, setHighlightedRouteId] = useState<string | null>(null);
    const [selectedContainerId, setSelectedContainerId] = useState<string | null>(null);
    const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [receiver, setReceiver] = useState<any>(null);

    // Avanzamento ogni 10s - riavvia a 0 quando raggiunge 100%
    useEffect(() => {
        const interval = setInterval(() => {
            setProgressById((prev) => {
                const next: Record<string, number> = {};
                for (const [id, p] of Object.entries(prev)) {
                    const newProgress = p + 0.05;
                    next[id] = newProgress >= 1 ? 0 : newProgress;
                }
                return next;
            });
            setLastUpdateTime(new Date());
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    const routes = useMemo(() => {
        return all
            .map((c, idx) => {
                // Ottieni waypoints dal container o genera automaticamente
                const waypoints = c.waypoints && c.waypoints.length > 0
                    ? c.waypoints
                    : generateRealisticRoute(c.route.from, c.route.to, c.createdAt);

                if (waypoints.length === 0) return null;

                // Estrai tutte le coordinate dai waypoints
                const coordinates = waypoints.map((w) => [w.lat, w.lng] as [number, number]);
                const from = coordinates[0];
                const to = coordinates[coordinates.length - 1];

                if (!from || !to) return null;

                const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
                return { c, waypoints, coordinates, from, to, color } as const;
            })
            .filter(Boolean) as Array<{
                c: Container;
                waypoints: typeof all[0]['waypoints'];
                coordinates: [number, number][];
                from: [number, number];
                to: [number, number];
                color: string
            }>;
    }, [all]);

    const allPoints = useMemo(() => routes.flatMap((r) => r.coordinates), [routes]);


    const mapRef = useRef<HTMLDivElement | null>(null);

    const formatDateTime = (date: Date) => {
        return new Intl.DateTimeFormat(navigator.language, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    // Calcola altezza disponibile: viewport - header nav (72px) - page header (circa 80px)
    const mapHeight = useMemo(() => {
        return window.innerHeight - 72 - 80;
    }, []);

    // Carica documenti e receiver quando selectedContainerId cambia
    useEffect(() => {
        if (selectedContainerId) {
            const containerData = containerService.getById(selectedContainerId);
            if (containerData) {
                const documentsData = documentService.getByContainerId(selectedContainerId);
                setDocuments(documentsData);
                const receiverData = accountService.getById(containerData.receiverId);
                setReceiver(receiverData);
            }
        } else {
            setDocuments([]);
            setReceiver(null);
        }
    }, [selectedContainerId]);

    // Reset tutti gli stati quando il pannello si chiude per ripristinare i colori
    useEffect(() => {
        if (!isListPanelOpen) {
            setSelectedContainerId(null);
            setHoveredContainerId(null);
            setHighlightedRouteId(null);
            setSelectedRouteId(null); // Nascondi route quando si chiude il pannello
        }
    }, [isListPanelOpen]);

    // Funzione helper per determinare se c'è almeno un container attivo
    const hasAnyActiveContainer = (): boolean => {
        return hoveredContainerId !== null ||
            selectedContainerId !== null ||
            highlightedRouteId !== null;
    };

    // Funzione helper per determinare se un container è attivo
    const isContainerActive = (containerId: string): boolean => {
        return hoveredContainerId === containerId ||
            selectedContainerId === containerId ||
            highlightedRouteId === containerId;
    };

    // Funzione per convertire colore in grigio
    const getInactiveColor = (): string => {
        return '#6b7280'; // gray-500
    };

    return (
        <>
            {/* Wrapper principale con layout flex per effetto push */}
            <div
                className="flex flex-row -mx-4 -my-6 sm:-mx-6 lg:-mx-8 gap-0 mt-2 relative"
            >
                {/* Pannello lista container - effetto push (non overlay) - SINISTRA */}
                <div
                    className={`bg-primary_alt shadow-xl transition-all duration-300 flex-shrink-0 overflow-hidden flex flex-col ${isListPanelOpen ? 'w-[400px]' : 'w-0'
                        }`}
                    style={{
                        height: mapHeight + 80, // Altezza mappa + header pagina
                    }}
                >
                    {isListPanelOpen && (
                        <div className="flex flex-col h-full w-full">
                            {/* Header */}
                            <div className="relative px-6 pt-6 pb-4 border-b border-secondary_alt flex-shrink-0">
                                <div>
                                    <h3 className="text-lg font-medium text-brand-primary">{t('worldMap.containerList')}</h3>
                                    <p className="mt-0.5 text-xs text-tertiary">
                                        {routes.length} {routes.length === 1 ? t('worldMap.containerSingular') : t('worldMap.containerPlural')}
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setIsListPanelOpen(false);
                                        setHoveredContainerId(null);
                                        setSelectedContainerId(null);
                                        setHighlightedRouteId(null);
                                        setSelectedRouteId(null); // Nascondi route
                                    }}
                                    className="absolute top-3 right-3 text-tertiary hover:text-primary transition-colors"
                                >
                                    <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Content scrollabile */}
                            <div className="flex-1 overflow-y-auto px-4 md:px-6 pb-6">
                                {selectedContainerId ? (
                                    // Vista dettaglio container
                                    (() => {
                                        const container = containerService.getById(selectedContainerId);
                                        if (!container) return null;

                                        return (
                                            <div className="space-y-6 pt-4">
                                                {/* Header con pulsante torna alla lista */}
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            onClick={() => {
                                                                setSelectedContainerId(null);
                                                                setHighlightedRouteId(null);
                                                                setSelectedRouteId(null); // Nascondi route
                                                            }}
                                                            className="text-tertiary hover:text-primary transition-colors"
                                                        >
                                                            <ArrowLeft className="size-5" />
                                                        </button>
                                                        <div>
                                                            <h3 className="text-lg font-semibold text-primary">{container.containerNumber}</h3>
                                                            <p className="text-sm text-tertiary">
                                                                {container.route.from} → {container.route.to}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <Link
                                                        to={`/shipper/containers/${container.id}`}
                                                        className="text-tertiary hover:text-primary transition-colors"
                                                        title="Apri dettaglio container"
                                                    >
                                                        <LinkExternal01 className="size-5" />
                                                    </Link>
                                                </div>

                                                {/* Container Info Panel migliorato */}
                                                <div className="space-y-4">
                                                    {/* Header con carrier e timestamp */}
                                                    <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <div className="flex items-center gap-3">
                                                                {container.shippingCompany && (
                                                                    <div className="size-10 rounded bg-brand-100 dark:bg-brand-900 flex items-center justify-center">
                                                                        <span className="text-xs font-semibold text-brand-600 dark:text-brand-400">
                                                                            {container.shippingCompany.substring(0, 2).toUpperCase()}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <h4 className="text-md font-semibold text-primary">{container.containerNumber}</h4>
                                                                    {container.shippingCompany && (
                                                                        <p className="text-sm text-tertiary">{container.shippingCompany}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-tertiary">
                                                            <Clock className="size-4" />
                                                            <span>Location of Vessel as of {formatDateTime(lastUpdateTime).replace(/,/g, '')}</span>
                                                        </div>
                                                    </div>

                                                    {/* Containers on vessel */}
                                                    <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                                                        <h4 className="text-md font-semibold text-primary mb-3">Containers on vessel</h4>
                                                        <div className="space-y-2">
                                                            <div className="rounded-lg bg-secondary p-3 ring-1 ring-secondary_alt">
                                                                <div className="flex items-start justify-between mb-2">
                                                                    <div className="flex-1">
                                                                        <div className="text-sm font-semibold text-primary mb-1">{container.containerNumber}</div>
                                                                        <div className="flex items-center gap-2 mb-1">
                                                                            <div className="flex items-center gap-1.5">
                                                                                <div className="size-2 rounded-full bg-brand-600"></div>
                                                                                <span className="text-xs text-primary">{container.route.from}</span>
                                                                            </div>
                                                                            <span className="text-xs text-tertiary">→</span>
                                                                            <div className="flex items-center gap-1.5">
                                                                                <div className="size-2 rounded-full bg-gray-400"></div>
                                                                                <span className="text-xs text-primary">{container.route.to}</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="text-xs text-tertiary">
                                                                            ETA {container.eta.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                                                                        </div>
                                                                        {receiver && (
                                                                            <div className="text-xs text-tertiary mt-1">
                                                                                {receiver.legalName}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <Badge color={getStatusColor(container.status)} size="sm">
                                                                        {translateContainerStatus(container.status)}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Container Info dettagliato */}
                                                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                                    <h4 className="text-lg font-semibold text-primary mb-4">{t('detail.info.containerInfo')}</h4>
                                                    <div className="space-y-4">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm text-tertiary">{t('detail.info.fields.status')}</span>
                                                            <Badge color={getStatusColor(container.status)} size="sm">
                                                                {translateContainerStatus(container.status)}
                                                            </Badge>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm text-tertiary">{t('detail.info.fields.progress')}</span>
                                                            <div className="flex items-center gap-2">
                                                                <ProgressBar
                                                                    value={container.progress}
                                                                    min={0}
                                                                    max={100}
                                                                />
                                                                <span className="text-sm text-primary">{container.progress}%</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm text-tertiary">{t('detail.info.fields.riskLevel')}</span>
                                                            <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                                                {translateRiskLevel(container.riskLevel)}
                                                            </Badge>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm text-tertiary">{t('detail.info.fields.documentStatus')}</span>
                                                            <Badge color={getDocumentStatusColor(container.documentStatus)} size="sm">
                                                                {translateDocumentStatus(container.documentStatus)}
                                                            </Badge>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm text-tertiary">{t('detail.info.fields.eta')}</span>
                                                            <span className="text-sm text-primary">
                                                                {container.eta.toLocaleDateString('it-IT')}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm text-tertiary">{t('detail.info.fields.value')}</span>
                                                            <span className="text-sm text-primary">
                                                                {container.value.currency} {container.value.amount.toLocaleString()}
                                                            </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Receiver Info */}
                                                {receiver && (
                                                    <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                                        <h4 className="text-lg font-semibold text-primary mb-4">{t('detail.info.receiver')}</h4>
                                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                            <div>
                                                                <div className="text-sm font-medium text-primary">{receiver.legalName}</div>
                                                                <div className="text-sm text-tertiary">{receiver.email}</div>
                                                            </div>
                                                            <div>
                                                                <div className="text-sm text-tertiary">{t('detail.info.fields.country')}</div>
                                                                <div className="text-sm text-primary">{receiver.country}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Documents List */}
                                                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                                    <h4 className="text-lg font-semibold text-primary mb-4">{t('detail.documents.title')}</h4>
                                                    {documents.length > 0 ? (
                                                        <div className="space-y-3">
                                                            {documents.map((document) => (
                                                                <div
                                                                    key={document.id}
                                                                    className="flex items-center justify-between p-3 rounded-lg bg-secondary ring-1 ring-secondary_alt"
                                                                >
                                                                    <div className="flex-1">
                                                                        <div className="text-sm font-medium text-primary">{document.name}</div>
                                                                        <div className="flex items-center gap-2 mt-1">
                                                                            <Badge color="brand" size="sm">
                                                                                {documentService.getDocumentTypeDisplayName(document.type)}
                                                                            </Badge>
                                                                            <Badge color={getDocumentStatusColor(document.status)} size="sm">
                                                                                {translateDocumentStatus(document.status)}
                                                                            </Badge>
                                                                        </div>
                                                                        <div className="text-xs text-tertiary mt-1">
                                                                            {document.uploadedAt.toLocaleDateString('it-IT')}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-tertiary">Nessun documento disponibile</p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()
                                ) : (
                                    // Lista container
                                    <div className="space-y-3 pt-4">
                                        {routes.map(({ c, color }) => {
                                            const progress = Math.round((progressById[c.id] ?? 0) * 100);
                                            const isHovered = hoveredContainerId === c.id;
                                            const isSelected = highlightedRouteId === c.id;

                                            return (
                                                <div
                                                    key={c.id}
                                                    className={`rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary cursor-pointer transition-all ${isHovered || isSelected
                                                        ? 'ring-2 shadow-md'
                                                        : 'hover:ring-2 hover:shadow-sm'
                                                        }`}
                                                    style={{
                                                        borderLeft: `4px solid ${color}`,
                                                    }}
                                                    onMouseEnter={() => setHoveredContainerId(c.id)}
                                                    onMouseLeave={() => setHoveredContainerId(null)}
                                                    onClick={() => {
                                                        setSelectedContainerId(c.id);
                                                        setHighlightedRouteId(c.id);
                                                        setSelectedRouteId(c.id); // Mostra route al click
                                                    }}
                                                >
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div className="flex-1">
                                                            <div className="flex items-center justify-between mb-1">
                                                                <h4 className="text-md font-semibold text-primary">{c.containerNumber}</h4>
                                                                <Badge color={getDocumentStatusColor(c.documentStatus)} size="sm">
                                                                    Docs Status: {translateDocumentStatus(c.documentStatus)}
                                                                </Badge>
                                                            </div>
                                                            <p className="text-sm text-tertiary">
                                                                {c.route.from} → {c.route.to}
                                                            </p>
                                                            <p className="text-xs text-tertiary mt-1">{c.shippingCompany ?? ""}</p>
                                                        </div>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <div className="flex items-center justify-between text-xs">
                                                            <span className="text-tertiary">Progress</span>
                                                            <span className="text-primary font-medium">{progress}%</span>
                                                        </div>
                                                        <ProgressBar value={progress} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Container mappa che si ridimensiona quando pannello è aperto */}
                <div
                    className="flex flex-col flex-1 transition-all duration-300 overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-secondary bg-secondary flex-shrink-0 shadow-xs ring-1 ring-secondary rounded-t-xl">
                        <div className="flex items-center gap-4">
                            <Link
                                to="/shipper/containers"
                                className="text-tertiary hover:text-primary transition-colors"
                            >
                                <ArrowLeft className="size-5" />
                            </Link>
                            <div>
                                <h1 className="text-2xl font-semibold text-primary">{t('worldMap.title')}</h1>
                                <p className="text-tertiary mt-1 text-sm">
                                    {t('worldMap.updateInfo')}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="text-sm text-tertiary bg-primary rounded-lg px-3 py-2 ring-1 ring-secondary shadow-xs">
                                <span className="text-tertiary">{t('worldMap.lastUpdate')}: </span>
                                <span className="text-primary font-medium">{formatDateTime(lastUpdateTime)}</span>
                            </div>
                            {!isListPanelOpen && (
                                <Button
                                    size="md"
                                    color="secondary"
                                    iconLeading={List}
                                    onClick={() => setIsListPanelOpen(true)}
                                >
                                    {t('worldMap.showContainers')} ({routes.length})
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Map Container - ALTEZZA FISSA definita */}
                    <div
                        ref={mapRef}
                        className="relative w-full rounded-xl"
                        style={{
                            height: '70vh',
                            minHeight: '70vh',
                        }}
                    >
                        {/* Popup info panel sulla mappa */}
                        {selectedContainerId && !isListPanelOpen && (() => {
                            const container = containerService.getById(selectedContainerId);
                            if (!container) return null;
                            
                            return (
                                <div className="absolute top-4 right-4 z-[1000] max-w-md">
                                    <ContainerInfoPanel
                                        container={container}
                                        lastUpdateTime={lastUpdateTime}
                                        onClose={() => {
                                            setSelectedContainerId(null);
                                            setHighlightedRouteId(null);
                                            setSelectedRouteId(null);
                                        }}
                                        onContainerSelect={(id) => {
                                            setSelectedContainerId(id);
                                            setHighlightedRouteId(id);
                                            setSelectedRouteId(id);
                                            if (!isListPanelOpen) {
                                                setIsListPanelOpen(true);
                                            }
                                        }}
                                    />
                                </div>
                            );
                        })()}
                        {mapHeight > 0 && (
                            <MapContainer
                                style={{ height: "100%", width: "100%", borderRadius: '12px' }}
                                center={[20, 0]}
                                zoom={2}
                                worldCopyJump
                                scrollWheelZoom={true}
                                whenReady={() => {
                                    // Forza invalidateSize quando la mappa è pronta
                                    setTimeout(() => {
                                        const leafletMap = (window as any).L?.map(mapRef.current?.querySelector('.leaflet-container') || undefined);
                                        if (leafletMap) {
                                            leafletMap.invalidateSize();
                                        }
                                    }, 50);
                                }}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />

                                {/* Route lines - mostrare solo se selezionata */}
                                {selectedRouteId && routes
                                    .filter(({ c }) => c.id === selectedRouteId)
                                    .map(({ c, coordinates, color }) => {
                                    const isActive = isContainerActive(c.id);
                                        const lineColor = isActive ? color : color;
                                        const lineOpacity = 1;
                                        const lineWeight = isActive ? 6 : 3;

                                    return (
                                        <Polyline
                                            key={`line-${c.id}`}
                                            positions={coordinates}
                                            pathOptions={{
                                                color: lineColor,
                                                weight: lineWeight,
                                                opacity: lineOpacity,
                                            }}
                                        />
                                    );
                                })}


                                {/* Ship markers */}
                                {routes.map(({ c, coordinates, color }) => {
                                    const t = progressById[c.id] ?? 0;

                                    // Calcolo robusto della posizione della nave lungo la rotta
                                    let totalDistance = 0;
                                    const segmentDistances: number[] = [];
                                    for (let i = 0; i < coordinates.length - 1; i++) {
                                        const dLat = coordinates[i + 1][0] - coordinates[i][0];
                                        const dLng = coordinates[i + 1][1] - coordinates[i][1];
                                        const dist = Math.sqrt(dLat * dLat + dLng * dLng);
                                        segmentDistances.push(dist);
                                        totalDistance += dist;
                                    }

                                    // Fallback: se distanza totale zero o coordinate insufficienti, usa il primo punto
                                    let shipPos: LatLngExpression = coordinates[0];
                                    if (coordinates.length >= 2 && totalDistance > 0) {
                                        const targetDistance = t * totalDistance;
                                        let accumulatedDistance = 0;

                                        for (let i = 0; i < segmentDistances.length; i++) {
                                            const seg = segmentDistances[i];
                                            if (seg === 0) {
                                                // Segmento degenerato, passa al successivo
                                                continue;
                                            }
                                            if (accumulatedDistance + seg >= targetDistance) {
                                                const segmentT = (targetDistance - accumulatedDistance) / seg;
                                                shipPos = interpolate(coordinates[i], coordinates[i + 1], segmentT) as LatLngExpression;
                                                break;
                                            }
                                            accumulatedDistance += seg;
                                            if (i === segmentDistances.length - 1) {
                                                shipPos = coordinates[coordinates.length - 1];
                                            }
                                        }
                                    }

                                    // Valida coordinate finali (evita Marker con NaN)
                                    const [lat, lng] = shipPos as [number, number];
                                    const isValidPos = Number.isFinite(lat) && Number.isFinite(lng);
                                    if (!isValidPos) {
                                        return null;
                                    }

                                    const hasActive = hasAnyActiveContainer();
                                    const isActive = isContainerActive(c.id);

                                    // Se non c'è nessun container attivo, tutti a colori
                                    // Se c'è almeno uno attivo, solo quello attivo è a colori, gli altri grigi
                                    const markerColor = !hasActive || isActive ? color : getInactiveColor();
                                    const markerOpacity = !hasActive || isActive ? 1 : 0.2;

                                    // Icona con highlight se attivo (più grande con bordo bianco)
                                    const markerIcon = isActive
                                        ? L.divIcon({
                                            html: `
                                            <div style="opacity: ${markerOpacity};">
                                                <svg width="40" height="50" viewBox="0 0 40 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <!-- Bordo bianco (path più grande sotto) -->
                                                    <path d="M20 2C9.954 2 2 9.954 2 20C2 30.046 20 50 20 50C20 50 38 30.046 38 20C38 9.954 30.046 2 20 2Z" fill="white"/>
                                                    <!-- Spilla principale colorata -->
                                                    <path d="M20 4C10.954 4 4 10.954 4 20C4 29.046 20 48 20 48C20 48 36 29.046 36 20C36 10.954 29.046 4 20 4Z" fill="${markerColor}"/>
                                                    <!-- Cerchio bianco al centro -->
                                                    <circle cx="20" cy="20" r="7" fill="white"/>
                                                </svg>
                                            </div>
                                        `,
                                            className: "",
                                            iconSize: [40, 50],
                                            iconAnchor: [20, 50],
                                        })
                                        : L.divIcon({
                                            html: `
                                            <div style="opacity: ${markerOpacity};">
                                                <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <!-- Spilla a goccia classica -->
                                                    <path d="M16 0C7.163 0 0 7.163 0 16C0 24.837 16 40 16 40C16 40 32 24.837 32 16C32 7.163 24.837 0 16 0Z" fill="${markerColor}"/>
                                                    <!-- Cerchio bianco al centro -->
                                                    <circle cx="16" cy="16" r="6" fill="white"/>
                                                </svg>
                                            </div>
                                        `,
                                            className: "",
                                            iconSize: [32, 40],
                                            iconAnchor: [16, 40],
                                        });

                                    return (
                                        <Marker
                                            key={`route-${c.id}`}
                                            position={shipPos}
                                            icon={markerIcon}
                                            zIndexOffset={isActive ? 1000 : 0}
                                            eventHandlers={{
                                                click: () => {
                                                    setSelectedContainerId(c.id);
                                                    setHighlightedRouteId(c.id);
                                                    setSelectedRouteId(c.id); // Mostra route al click
                                                    // Apri la lista se non è già aperta
                                                    if (!isListPanelOpen) {
                                                        setIsListPanelOpen(true);
                                                    }
                                                },
                                            }}
                                        />
                                    );
                                })}

                                <FitBounds
                                    points={allPoints}
                                    isPanelOpen={isListPanelOpen}
                                    selectedContainerId={selectedContainerId}
                                    routes={routes}
                                />
                                <MapResizeHandler wrapperRef={mapRef} isPanelOpen={isListPanelOpen} />
                            </MapContainer>
                        )}
                    </div>
                </div>

            </div>

        </>
    );
}

export default ShipperContainersWorldMapPage;
